import React, { useState, useEffect, useRef, useMemo } from 'react';
import { formatCurrency } from '../../utils/currency';
import { formatCountdown } from '../../utils/formatCountdown';
import { getPlanningIntentConfig } from '../../utils/planningIntent';

const PlanCard = ({
  plan,
  onSelect,
  onDelete,
  onDuplicate,
  onShare,
  onEditName,
  isSelected = false,
  onSelectChange,
  showActions = false,
  onToggleActions,
  highlight = false,
  onToggleFavorite
}) => {
  // Use controlled state if provided, otherwise use local state
  const [localShowActions, setLocalShowActions] = useState(false);
  const showActionsMenu = onToggleActions ? showActions : localShowActions;

  // Expand/collapse state
  const [isExpanded, setIsExpanded] = useState(false);
  const cardRef = useRef(null);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 640);

  // Handle click outside to collapse
  useEffect(() => {
    if (!isExpanded) return;

    const handleClickOutside = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) {
        setIsExpanded(false);
      }
    };

    // Add slight delay to avoid immediate collapse
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  // Track window width for responsive card height
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleExpand = (e) => {
    e.stopPropagation();
    setIsExpanded(true);
  };

  const handleCollapse = (e) => {
    if (e) e.stopPropagation();
    setIsExpanded(false);
  };
  const intentConfig = getPlanningIntentConfig(plan.event_data?.focus_selection);
  const eventType =
    plan.event_data?.custom_event_type ||
    plan.event_data?.event_type ||
    'Event';
  const hasThemes = plan.themes && plan.themes.length > 0;
  const selectedTheme = plan.selected_theme;
  const themeCount = plan.themes?.length || 0;
  const isDraft = !hasThemes;
  const isCompleted = selectedTheme && (
    intentConfig.key === 'theme_ideas' || 
    (plan.shopping_items && plan.shopping_items.length > 0)
  );

  const getPrimaryActionLabel = () => {
    if (!isCompleted) {
      if (hasThemes && !selectedTheme) {
        switch (intentConfig.key) {
          case 'food':
            return 'Choose menu concept';
          case 'decor':
            return 'Choose decor concept';
          case 'activities':
            return 'Choose activity concept';
          case 'shopping':
            return 'Choose shopping direction';
          case 'theme_ideas':
            return 'Choose concept';
          case 'mixed':
            return 'Choose plan concept';
          case 'everything':
          default:
            return 'Choose theme';
        }
      }
      return 'Continue planning';
    }
    
    switch (intentConfig.key) {
      case 'theme_ideas':
        return 'View board';
      case 'decor':
        return 'View decor plan';
      case 'food':
        return 'View food plan';
      case 'activities':
        return 'View activities plan';
      case 'shopping':
        return 'View shopping list';
      case 'mixed':
        return 'View custom plan';
      case 'everything':
      default:
        return 'View plan';
    }
  };

  const showSecondaryButton = isCompleted && hasThemes && 
    !selectedTheme?.catalog_quick_workflow && 
    !plan.themes?.some(t => t.catalog_quick_workflow);

  const getSecondaryActionLabel = () => {
    switch (intentConfig.key) {
      case 'food':
        return 'View menu concepts';
      case 'decor':
        return 'View decor concepts';
      case 'theme_ideas':
        return 'Concepts';
      case 'mixed':
        return 'Concepts';
      case 'activities':
        return 'View activity concepts';
      case 'shopping':
        return 'View shopping concepts';
      case 'everything':
      default:
        return 'Themes';
    }
  };

  const secondaryActionLabel = getSecondaryActionLabel();

  const getEventTypeIcon = (type) => {
    const icons = {
      'Birthday': '🎂',
      'Wedding': '💒',
      'Baby Shower': '👶',
      'Graduation': '🎓',
      'Anniversary': '💍',
      'Corporate': '🏢',
      'Holiday': '🎄',
      'Other': '🎉'
    };
    return icons[type] || '🎉';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    const now = new Date();

    // Get dates at start of day in local timezone for accurate day comparison
    const dateYear = date.getFullYear();
    const dateMonth = date.getMonth();
    const dateDay = date.getDate();
    const nowYear = now.getFullYear();
    const nowMonth = now.getMonth();
    const nowDay = now.getDate();

    // Create date objects at midnight for comparison
    const dateStart = new Date(dateYear, dateMonth, dateDay);
    const nowStart = new Date(nowYear, nowMonth, nowDay);

    // Calculate difference in calendar days (positive means date is in the past)
    const diffTime = nowStart.getTime() - dateStart.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    // Handle edge cases
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays > 1 && diffDays < 7) return `${diffDays} days ago`;
    if (diffDays >= 7 && diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    // For future dates (shouldn't happen), show "Today" as fallback
    if (diffDays < 0) return 'Today';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: dateYear !== nowYear ? 'numeric' : undefined });
  };

  const getStatusColor = () => {
    if (isDraft) return 'bg-stone-100 text-stone-600 border-stone-200';
    if (isCompleted) return 'bg-emerald-50 text-emerald-700 border-emerald-200/80';
    if (hasThemes && !selectedTheme) return 'bg-amber-50 text-amber-800 border-amber-200/80';
    return 'bg-blue-50 text-blue-700 border-blue-200/80';
  };

  const getStatusLabel = () => {
    if (isDraft) return 'Draft';
    if (isCompleted) {
      return intentConfig.key === 'theme_ideas' ? 'Concept Ready' : 'Plan Ready';
    }
    if (hasThemes && !selectedTheme) {
      return intentConfig.key === 'everything' ? 'Themes Ready' : 'Concepts Ready';
    }
    return 'In Progress';
  };

  // Get plan name from event_data or use event type
  const planName = plan.event_data?.plan_name || plan.event_data?.event_type || 'Untitled Plan';
  // Truncate long names (50 chars should be enough for most screens)
  const displayName = planName.length > 50 ? `${planName.substring(0, 50)}...` : planName;

  // Shopping progress
  const shoppingItems = plan.shopping_items || [];
  const totalItems = shoppingItems.length;
  const shoppedItems = shoppingItems.filter(item => item.purchased || item.checked).length;
  const shoppingProgress = totalItems > 0 ? `${shoppedItems}/${totalItems}` : null;

  const parsePrice = (priceStr) => {
    if (priceStr === null || priceStr === undefined) return 0;
    const n = parseFloat(String(priceStr).replace(/[^0-9.]/g, ''));
    return Number.isFinite(n) ? n : 0;
  };

  const estTotals = useMemo(() => {
    let buyTotal = 0;
    let optimizedTotal = 0;

    shoppingItems.forEach(item => {
      if (!item.purchased) {
        const buyPrice = parsePrice(item.buy_price);
        buyTotal += buyPrice;

        if (item.rentable && item.rent_price) {
          optimizedTotal += parsePrice(item.rent_price);
        } else {
          optimizedTotal += buyPrice;
        }
      }
    });

    return { buyTotal, optimizedTotal };
  }, [shoppingItems]);

  // Money spent (placeholder - will need backend support)
  const budget = plan.event_data?.budget || 0;
  const moneySpent = plan.event_data?.money_spent || 0; // TODO: Track this from user input
  const budgetCountry = plan.event_data?.country || 'US';
  const formatMoney = (amount) => formatCurrency(amount || 0, budgetCountry);

  // Shared count (placeholder - will need backend support)
  const sharedCount = plan.shared_with?.length || 0;

  // Event date
  const eventDate = plan.event_data?.date;
  const countdown = eventDate ? formatCountdown(eventDate) : null;

  const getThemesGeneratedLabel = () => {
    switch (intentConfig.key) {
      case 'food':
        return 'Menu Concepts Generated';
      case 'decor':
        return 'Decor Concepts Generated';
      case 'activities':
        return 'Activity Concepts Generated';
      case 'shopping':
        return 'Shopping Directions Generated';
      case 'theme_ideas':
        return 'Concepts Generated';
      case 'mixed':
        return 'Plan Concepts Generated';
      case 'everything':
      default:
        return 'Themes Generated';
    }
  };

  const getThemesCountLabel = () => {
    const isTheme = intentConfig.key === 'everything';
    const isDirection = intentConfig.key === 'shopping';
    
    if (isTheme) {
      return `${themeCount} theme${themeCount === 1 ? '' : 's'}`;
    }
    if (isDirection) {
      return `${themeCount} direction${themeCount === 1 ? '' : 's'}`;
    }
    return `${themeCount} concept${themeCount === 1 ? '' : 's'}`;
  };

  const getStatusDotClass = () => {
    if (isDraft) return 'bg-stone-400';
    if (isCompleted) return 'bg-emerald-500';
    if (hasThemes && !selectedTheme) return 'bg-amber-500';
    return 'bg-blue-500';
  };

  const coverImage = selectedTheme?.ai_image_url || selectedTheme?.image_url || plan.themes?.[0]?.image_url;

  const renderDropdown = () => (
    <div
      className="absolute right-0 top-full mt-1 w-52 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50 plan-card-actions-dropdown"
      onClick={(e) => e.stopPropagation()}
    >
      {onEditName && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEditName(plan);
            if (onToggleActions) {
              onToggleActions(null);
            } else {
              setLocalShowActions(false);
            }
          }}
          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition flex items-center gap-2 focus:outline-none focus:ring-0"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          Edit Name
        </button>
      )}
      {onDuplicate && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDuplicate(plan);
            if (onToggleActions) {
              onToggleActions(null);
            } else {
              setLocalShowActions(false);
            }
          }}
          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition flex items-center gap-2 focus:outline-none focus:ring-0"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          Duplicate
        </button>
      )}
      {onShare && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onShare(plan);
            if (onToggleActions) {
              onToggleActions(null);
            } else {
              setLocalShowActions(false);
            }
          }}
          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition flex items-center gap-2 focus:outline-none focus:ring-0"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
          Share
        </button>
      )}
      {onDelete && (
        <>
          <div className="border-t border-gray-200 my-1"></div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(plan.plan_id, e);
              if (onToggleActions) {
                onToggleActions(null);
              } else {
                setLocalShowActions(false);
              }
            }}
            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition flex items-center gap-2 focus:outline-none focus:ring-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete
          </button>
        </>
      )}
    </div>
  );

  return (
    <>
      {/* Card Container */}
      <div
        ref={cardRef}
        className={`
          bg-white rounded-2xl border transition-all duration-300 group cursor-pointer relative w-full
          mx-auto sm:mx-0
          flex flex-col shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_12px_30px_rgba(0,0,0,0.1)]
          ${isSelected ? 'border-stone-900 ring-2 ring-stone-900/10' : 'border-stone-200/90 hover:border-stone-400/80'}
          ${highlight ? 'ring-4 ring-stone-400/30 border-stone-900 shadow-xl scale-[1.02] z-20' : ''}
        `}
        style={{
          minHeight: '340px',
          overflow: 'visible'
        }}
        onClick={(e) => {
          // Don't trigger select on button or menu click
          if (e.target.closest('button') || e.target.closest('.menu-container')) return;
          // Guard window access and disable whole-card navigation/selection on mobile/tablet (below 768px)
          if (typeof window !== 'undefined' && window.innerWidth < 768) return;
          if (onSelectChange) {
            onSelectChange(plan.plan_id, !isSelected);
          } else {
            onSelect(plan);
          }
        }}
      >
        {/* Card Header with Cover Banner if Available */}
        {coverImage ? (
          <div className="relative h-36 w-full overflow-hidden rounded-t-2xl bg-stone-100 flex-shrink-0 group/cover">
            <img
              src={coverImage}
              alt={selectedTheme?.name || planName}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/10" />

            {/* Floating badges top-left */}
            <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5 z-10 flex-wrap max-w-[70%]">
              {onSelectChange && (
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => {
                    e.stopPropagation();
                    onSelectChange(plan.plan_id, e.target.checked);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="w-4 h-4 text-stone-900 border-white/60 bg-white/90 rounded focus:ring-0 cursor-pointer shadow-sm"
                />
              )}
              <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border shadow-sm backdrop-blur-md ${getStatusColor()}`}>
                <span className={`w-1.5 h-1.5 rounded-full inline-block mr-1 ${getStatusDotClass()}`} />
                {getStatusLabel()}
              </span>
              <span className="px-2 py-0.5 text-[9px] font-medium rounded-full border border-white/30 bg-black/40 text-white backdrop-blur-md">
                {intentConfig.dashboardBadge}
              </span>
            </div>

            {/* Floating actions top-right */}
            <div className="absolute top-2.5 right-2.5 flex items-center gap-1 z-20">
              {onToggleFavorite && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onToggleFavorite(plan, !plan.is_favorite);
                  }}
                  className={`p-1.5 rounded-lg backdrop-blur-md border transition ${
                    plan.is_favorite
                      ? 'bg-amber-400/30 text-amber-300 border-amber-300/40 hover:bg-amber-400/40'
                      : 'bg-black/40 text-white/80 border-white/20 hover:bg-black/60 hover:text-white'
                  }`}
                  title={plan.is_favorite ? 'Remove from favorites' : 'Mark as favorite'}
                  aria-label={plan.is_favorite ? 'Remove from favorites' : 'Mark as favorite'}
                  type="button"
                >
                  <svg className="w-3.5 h-3.5" fill={plan.is_favorite ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                </button>
              )}

              {onShare && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onShare(plan);
                  }}
                  className="p-1.5 rounded-lg bg-black/40 backdrop-blur-md border border-white/20 text-white/80 hover:bg-black/60 hover:text-white transition"
                  title="Share plan"
                  type="button"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                </button>
              )}

              {/* Actions Menu */}
              <div className="relative menu-container flex-shrink-0">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (onToggleActions) {
                      onToggleActions(showActionsMenu ? null : plan.plan_id);
                    } else {
                      setLocalShowActions(!localShowActions);
                    }
                  }}
                  className="p-1.5 rounded-lg bg-black/40 backdrop-blur-md border border-white/20 text-white/80 hover:bg-black/60 hover:text-white transition plan-card-actions-menu"
                  title="More actions"
                  type="button"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                  </svg>
                </button>
                {showActionsMenu && renderDropdown()}
              </div>
            </div>

            {/* Bottom title & theme label inside cover */}
            <div className="absolute bottom-2.5 left-3 right-3 z-10">
              {selectedTheme?.name && (
                <div className="text-[10px] font-bold uppercase tracking-wider text-amber-300 line-clamp-1 drop-shadow">
                  ✨ {selectedTheme.name}
                </div>
              )}
              <h3 className="font-bold text-white text-base leading-snug line-clamp-1 drop-shadow-md" title={planName}>
                {displayName}
              </h3>
            </div>
          </div>
        ) : (
          /* Standard Card Header (no cover image) */
          <div className="p-3.5 sm:p-4 border-b border-stone-100 relative">
            {/* Row 1: Checkbox, Status, Planning Intent Badge */}
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-2 pr-[88px] min-w-0">
              {onSelectChange && (
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => {
                    e.stopPropagation();
                    onSelectChange(plan.plan_id, e.target.checked);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="w-4 h-4 text-stone-900 border-stone-300 rounded focus:ring-0 focus:ring-offset-0 outline-none cursor-pointer flex-shrink-0"
                />
              )}

              {/* Status Badge */}
              <span className={`
                px-2.5 py-0.5 text-xs font-semibold rounded-full border flex-shrink-0 flex items-center gap-1.5
                ${getStatusColor()}
              `}>
                <span className={`w-1.5 h-1.5 rounded-full ${getStatusDotClass()}`} />
                {getStatusLabel()}
              </span>

              {/* Planning Intent Badge */}
              <span className="px-2 py-0.5 text-[10px] sm:text-xs font-medium rounded-full border border-stone-200 bg-stone-50 text-stone-600 flex-shrink-0 whitespace-nowrap">
                {intentConfig.dashboardBadge}
              </span>
            </div>

            {/* Actions Area */}
            <div className="absolute top-3 right-3 sm:top-4 sm:right-4 flex items-center gap-1 sm:gap-1.5 z-10">
              {onToggleFavorite && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onToggleFavorite(plan, !plan.is_favorite);
                  }}
                  className={`p-1 rounded transition focus:outline-none focus:ring-0 flex-shrink-0 ${
                    plan.is_favorite
                      ? 'text-amber-400 hover:text-amber-500'
                      : 'text-gray-300 hover:text-amber-400'
                  }`}
                  title={plan.is_favorite ? 'Remove from favorites' : 'Mark as favorite'}
                  aria-label={plan.is_favorite ? 'Remove from favorites' : 'Mark as favorite'}
                  type="button"
                >
                  <svg className="w-4 h-4" fill={plan.is_favorite ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                </button>
              )}

              {onShare && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onShare(plan);
                  }}
                  className="p-1.5 text-gray-400 hover:text-zinc-900 hover:bg-stone-50 rounded transition focus:outline-none focus:ring-0 flex-shrink-0"
                  title="Share plan"
                  type="button"
                >
                  <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                </button>
              )}

              {/* Actions Menu */}
              <div className="relative menu-container flex-shrink-0">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (onToggleActions) {
                      onToggleActions(showActionsMenu ? null : plan.plan_id);
                    } else {
                      setLocalShowActions(!localShowActions);
                    }
                  }}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition focus:outline-none focus:ring-0 plan-card-actions-menu"
                  title="More actions"
                  type="button"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                  </svg>
                </button>
                {showActionsMenu && renderDropdown()}
              </div>
            </div>

            {/* Row 2: Plan Name */}
            <div className="mb-1 min-w-0">
              <h3 className="font-bold text-stone-900 text-sm sm:text-base leading-snug min-w-0 line-clamp-1 group-hover:text-stone-700 transition" title={planName}>
                {displayName}
              </h3>
            </div>
          </div>
        )}

        {/* Date Row (consistent location) */}
        <div className="px-3.5 sm:px-4 pt-2.5 pb-1 flex items-center justify-between text-[11px] text-stone-500 font-medium">
          {eventDate ? (
            <span className="flex items-center gap-1.5">
              <span>📅</span>
              <span>{new Date(eventDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
              {countdown && <span className="text-amber-800 font-semibold bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200/60">• {countdown}</span>}
            </span>
          ) : (
            <span className="flex items-center gap-1 text-stone-400">📅 Date: TBD</span>
          )}
        </div>

        {/* Card Body */}
        <div className="p-3 sm:p-4 flex flex-col flex-1 min-h-0">
          <div className="space-y-2.5 flex-1 flex flex-col">
            {/* Event Specs */}
            <div className="pt-2 border-t border-gray-100 space-y-1.5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">Event Type</span>
                <span className="font-semibold text-gray-900 flex items-center gap-1">
                  <span>{getEventTypeIcon(eventType)}</span>
                  <span>{eventType}</span>
                </span>
              </div>
              {plan.event_data?.guest_count !== null &&
                plan.event_data?.guest_count !== undefined &&
                plan.event_data?.guest_count !== '' &&
                Number(plan.event_data?.guest_count) > 0 && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Guests</span>
                    <span className="font-semibold text-gray-900">{plan.event_data.guest_count} guests</span>
                  </div>
                )}
            </div>

            {/* Costs & Estimates */}
            {(Number(budget) > 0 || totalItems > 0) && (
              <div className="pt-2 border-t border-gray-100 space-y-1.5">
                {Number(budget) > 0 && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Event Budget</span>
                    <span className="font-bold text-gray-900">{formatMoney(budget)}</span>
                  </div>
                )}
                {totalItems > 0 && (
                  <>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-zinc-800 font-bold">Est. Rent-Optimized</span>
                      <span className="font-extrabold text-zinc-900">{formatMoney(estTotals.optimizedTotal)}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-green-700 font-bold">Est. Buy All</span>
                      <span className="font-extrabold text-green-700">{formatMoney(estTotals.buyTotal)}</span>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Themes & Progress Info */}
            <div className="pt-2 border-t border-gray-100 space-y-1.5">
              {hasThemes && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600">{getThemesGeneratedLabel()}</span>
                  <span className="font-semibold text-zinc-800">{getThemesCountLabel()}</span>
                </div>
              )}
              {shoppingProgress && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600">Shopping Progress</span>
                  <span className={`font-semibold ${shoppedItems === totalItems ? 'text-green-600' : 'text-amber-600'}`}>
                    {shoppingProgress} items
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between text-[11px] text-gray-500 pt-1">
                <span>Last Updated:</span>
                <span>{formatDate(plan.updated_at || plan.created_at)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Footer - Action Buttons */}
        <div className="px-3.5 sm:px-4 pb-3.5 sm:pb-4 pt-3 border-t border-stone-100 flex-shrink-0 flex flex-col gap-1.5 w-full">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSelect(plan, 'plan');
            }}
            className="w-full text-xs font-bold bg-stone-900 hover:bg-stone-800 text-white py-2.5 rounded-xl transition flex items-center justify-center gap-1.5 focus:outline-none focus:ring-0 shadow-sm active:scale-[0.99]"
          >
            {getPrimaryActionLabel()}
            <span className="transition-transform group-hover:translate-x-0.5">→</span>
          </button>
          {showSecondaryButton && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSelect(plan, 'carousel');
              }}
              className="w-full text-xs font-semibold text-stone-700 bg-stone-50 hover:bg-stone-100 border border-stone-200 hover:border-stone-300 py-2 rounded-xl transition text-center focus:outline-none focus:ring-0"
            >
              {secondaryActionLabel}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default PlanCard;
